import { useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import moment from "moment";
import { ITableColumn } from "../Table/TableColumn";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { TableWrapper, TableLoader, TableHeaderCell, Align } from "../Table/style";
import { IPnLRow } from "../../utils/pnl";
import { formatAmount } from "../../utils/strings";
import { getValueColor } from "./utils";

interface IPnLTableProps {
  pnlData: IPnLRow[];
  isLoading: boolean;
}

export function PnLTable({ pnlData, isLoading }: IPnLTableProps) {
  const columns: ITableColumn<IPnLRow>[] = useMemo(
    () => [
      {
        title: "Instrument Name",
        accessor: "InstrumentName",
        align: "left",
      },
      {
        title: "Strategy",
        accessor: "Strategy",
        align: "left",
      },
      {
        title: "Snapshot Start",
        accessor: "SnapshotStart",
        align: "left",
        Cell: ({ value }) => <div>{moment(value).format("YYYY-MM-DD HH:mm:ss")}</div>,
      },
      {
        title: "Snapshot End",
        accessor: "SnapshotEnd",
        align: "left",
        Cell: ({ value }) => <div>{moment(value).format("YYYY-MM-DD HH:mm:ss")}</div>,
      },
      {
        title: "Total Buy Volume",
        accessor: "TotalBuyVolume",
        align: "right",
        Cell: ({ value }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "Total Sell Volume",
        accessor: "TotalSellVolume",
        align: "right",
        Cell: ({ value }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "Total Buy Amount",
        accessor: "TotalBuyAmount",
        align: "right",
        Cell: ({ value }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "Total Sell Amount",
        accessor: "TotalSellAmount",
        align: "right",
        Cell: ({ value }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "Total Fees",
        accessor: "TotalFees",
        align: "right",
        Cell: ({ value }) => {
          const feesValue = Number(value);
          // Negative fees = rebate (good, green), Positive fees = cost (bad, red)
          const displayColor = feesValue < 0 ? getValueColor(Math.abs(feesValue)) : getValueColor(-feesValue);
          return (
            <Align
              align="right"
              style={{
                color: displayColor,
              }}
            >
              {formatAmount(feesValue)}
            </Align>
          );
        },
      },
      {
        title: "Avg Buy Price",
        accessor: "AverageBuyPrice",
        align: "right",
        Cell: ({ value }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "Avg Sell Price",
        accessor: "AverageSellPrice",
        align: "right",
        Cell: ({ value }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "PnL",
        accessor: "PnL",
        align: "right",
        Cell: ({ value }) => {
          const pnlValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(pnlValue),
              }}
            >
              {pnlValue > 0 ? "+" : ""}
              {formatAmount(pnlValue)}
            </Align>
          );
        },
      },
      {
        title: "Net PnL",
        accessor: "NetPnL",
        align: "right",
        Cell: ({ value }) => {
          const netPnLValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(netPnLValue),
                fontWeight: "bold",
              }}
            >
              {netPnLValue > 0 ? "+" : ""}
              {formatAmount(netPnLValue)}
            </Align>
          );
        },
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: pnlData,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
        initialState: {
          sortBy: [
            {
              id: "NetPnL",
              desc: true,
            },
          ],
        },
      },
      useSortBy
    );

  return (
    <TableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ width: column.width }}
                >
                  {column.render("Header")}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { ...rowProps } = row.getRowProps();
            return (
              <tr {...rowProps}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No PnL data available for the selected period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </TableWrapper>
  );
}
